// Positionen aus den Einträgen eines oder mehrerer Stundenzettel (ohne Oberfläche). Benutzt vom einzelnen Stundenzettel
// in der Rechnung (Stundenzettel.tsx), von mehreren Zetteln auf einmal (StundenzettelStapel.tsx) und von der Rechnung aus
// erfassten Zeiten (zeiten/ZeitDaten.ts, quelle „zeiten“).
import { fmtDate } from '../lib/format';
import { lineCents, parsePrice } from './formular';
import type { Rate } from './types';

export interface KiPosition {
  description: string;
  quantity: number;
  unit: string;
  price: string;
  // Steuersatz der Leistung, deren Preis die Position hat (nur mit preisFuer)
  rate?: Rate;
}

// Ein Eintrag mit Stunden (wie die KI ihn gelesen hat, Stunden vom Mandanten geprüft)
export interface StundenZeile {
  datum: string | null;
  name: string | null;
  taetigkeit: string | null;
  stunden: number;
}

// je Tätigkeit eine Position mit der Summe der Stunden (Standard bei mehreren Zetteln), je Eintrag eine, alles in einer
export type PositionsArt = 'taetigkeit' | 'einzeln' | 'summe';

const STD = 'Std.';
// mehr verschiedene Tätigkeiten: dann alles in einer Position
const TAETIGKEITEN_MAX = 20;
const round2 = (n: number) => Math.round(n * 100) / 100;

// erster und letzter Tag der Einträge
export function zeitraum(z: StundenZeile[]): { from: string | null; to: string | null } {
  const d = z
    .map((e) => e.datum)
    .filter((x): x is string => !!x)
    .sort();
  return { from: d[0] ?? null, to: d[d.length - 1] ?? null };
}
export const zeitraumText = (from: string | null, to: string | null) =>
  from ? (to && to !== from ? `${fmtDate(from)} – ${fmtDate(to)}` : fmtDate(from)) : '';

// „Unterhaltsreinigung“, „unterhaltsreinigung “ und „Unterhaltsreinigung.“ sind dieselbe Tätigkeit
export const taetigkeitKey = (t: string | null) => (t ?? '').toLowerCase().replace(/[\s.,;:]+/g, ' ').trim();

// Namen der Mitarbeiter nur, wenn der Kunde das verlangt (namen: true, nur je Eintrag). quelle „zeiten“: Text ohne
// „laut Stundenzettel“. preisFuer: eigener Preis und Steuersatz einer Tätigkeit (je Tätigkeit und je Eintrag; eine
// Position nimmt immer preis, ohne Satz). Mengen mit zwei Nachkommastellen; Gruppen summieren die ungerundeten Stunden
// und runden einmal.
export function stundenPositionen(
  zeilen: StundenZeile[],
  o: {
    art: PositionsArt;
    preis: string;
    namen?: boolean;
    quelle?: 'zettel' | 'zeiten';
    preisFuer?: (taetigkeit: string | null) => { price: string; rate: Rate } | null;
  },
): KiPosition[] {
  if (!zeilen.length) return [];
  const preisVon = (t: string | null): Pick<KiPosition, 'price' | 'rate'> => o.preisFuer?.(t) ?? { price: o.preis };
  if (o.art === 'einzeln') {
    return zeilen.map((e) => ({
      description: [e.datum ? fmtDate(e.datum) : '', e.taetigkeit ?? 'Arbeitsstunden', o.namen && e.name ? `(${e.name})` : '']
        .filter(Boolean)
        .join(' '),
      quantity: round2(e.stunden),
      unit: STD,
      ...preisVon(e.taetigkeit),
    }));
  }
  const gruppen = new Map<string, StundenZeile[]>();
  for (const e of zeilen) {
    const k = o.art === 'summe' ? '' : taetigkeitKey(e.taetigkeit);
    gruppen.set(k, [...(gruppen.get(k) ?? []), e]);
  }
  if (o.art === 'taetigkeit' && gruppen.size > TAETIGKEITEN_MAX) return stundenPositionen(zeilen, { ...o, art: 'summe' });
  return [...gruppen.values()].map((list) => {
    const tasks = [...new Set(list.map((e) => e.taetigkeit).filter(Boolean))];
    const { from, to } = zeitraum(list);
    const range = zeitraumText(from, to);
    const text = tasks.length === 1 ? tasks[0]! : 'Arbeitsstunden';
    return {
      description: `${text}${o.quelle === 'zeiten' ? '' : ' laut Stundenzettel'}${range ? `, ${range}` : ''}`,
      quantity: round2(list.reduce((s, e) => s + e.stunden, 0)),
      unit: STD,
      // je Tätigkeit: alle Einträge der Gruppe haben dieselbe Tätigkeit (taetigkeitKey)
      ...(o.art === 'summe' ? { price: o.preis } : preisVon(list[0]!.taetigkeit)),
    };
  });
}

// netto in Cent wie auf der Rechnung (jede Position für sich gerundet); null: Preis nicht lesbar
export function nettoCents(items: KiPosition[]): number | null {
  let sum = 0;
  for (const i of items) {
    const p = parsePrice(i.price);
    if (typeof p !== 'number') return null;
    sum += lineCents(i.quantity, p);
  }
  return sum;
}
